'use no memo'

import { Suspense, lazy, useMemo } from 'react'
import { ProjectListView } from './ProjectListView'
import { ProjectViewControls } from './ProjectViewControls'
import { ViewLoadingFallback } from './ViewLoadingFallback'
import useProjectViewPreferencesStore from '@/store/ProjectViewPreferencesStore'
import { usePageLayout } from '@/hooks/usePageLayout'

const ProjectBoardView = lazy(() =>
  import('./ProjectBoardView').then((m) => ({ default: m.ProjectBoardView })),
)
const ProjectCalendarView = lazy(() =>
  import('./ProjectCalendarView').then((m) => ({ default: m.ProjectCalendarView })),
)
const ProjectGanttView = lazy(() =>
  import('./ProjectGanttView').then((m) => ({ default: m.ProjectGanttView })),
)

interface ProjectViewRendererProps {
  projectId: string
  hideProjectFilter?: boolean
}

export function ProjectViewRenderer({
  projectId,
  hideProjectFilter,
}: ProjectViewRendererProps) {
  const viewType = useProjectViewPreferencesStore(
    (s) => s.preferences[projectId]?.viewType ?? 'list',
  )
  const density = useProjectViewPreferencesStore(
    (s) => s.preferences[projectId]?.density ?? 'simple',
  )

  const hiddenFields = useMemo(
    () => (hideProjectFilter ? (['project'] as const) : undefined),
    [hideProjectFilter],
  )

  const breadcrumbActions = useMemo(() => {
    if (viewType === 'calendar') {
      return (
        <div className="flex items-center gap-1">
          <ProjectViewControls
            projectId={projectId}
            hiddenFields={hiddenFields ? [...hiddenFields] : undefined}
          />
        </div>
      )
    }
    return (
      <ProjectViewControls
        projectId={projectId}
        hiddenFields={hiddenFields ? [...hiddenFields] : undefined}
      />
    )
  }, [projectId, viewType, hiddenFields])

  usePageLayout({ breadcrumbActions }, [projectId, viewType])

  if (viewType === 'board') {
    return (
      <Suspense fallback={<ViewLoadingFallback />}>
        <ProjectBoardView projectId={projectId} density={density} />
      </Suspense>
    )
  }

  if (viewType === 'calendar') {
    return (
      <Suspense fallback={<ViewLoadingFallback />}>
        <ProjectCalendarView projectId={projectId} density={density} />
      </Suspense>
    )
  }

  if (viewType === 'gantt') {
    return (
      <div className="flex-1 min-h-0 h-full">
        <Suspense fallback={<ViewLoadingFallback />}>
          <ProjectGanttView projectId={projectId} density={density} />
        </Suspense>
      </div>
    )
  }

  return <ProjectListView projectId={projectId} density={density} />
}
